import React, { useState, useEffect, useCallback } from "react";
import { useAuth, useTheme } from "@/App";
import api from "@/lib/api";
import {
  Shield, User, MapPin, Heart, UtensilsCrossed,
  Activity, Clock, MessageSquare, Send,
  ArrowLeft, ChevronRight, Check, TrendingUp,
  Moon as MoonIcon, Sun as SunIcon
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const depLabels = {
  autovalido: "Autoválido",
  dependencia_media: "Dependencia Media",
  dependencia_total: "Dependencia Total",
};

export default function FamilyDashboardPage() {
  const { user } = useAuth();
  const { darkMode, toggleDark } = useTheme();
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [residentDetail, setResidentDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgContent, setMsgContent] = useState("");
  const [sending, setSending] = useState(false);
  const [weeklyData, setWeeklyData] = useState(null);

  useEffect(() => {
    api.get("/family/my-residents")
      .then(r => { setResidents(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const loadResidentDetail = useCallback(async (rid) => {
    try {
      const [detailRes, msgsRes, weekRes] = await Promise.all([
        api.get(`/family/resident/${rid}`),
        api.get(`/family/messages/${rid}`),
        api.get(`/daily-care/resident/${rid}/week`)
      ]);
      setResidentDetail(detailRes.data);
      setMessages(msgsRes.data);
      setWeeklyData(weekRes.data);
    } catch { toast.error("Error al cargar datos"); }
  }, []);

  const handleSelectResident = (r) => {
    setSelectedResident(r);
    loadResidentDetail(r.id);
  };

  const handleSendMessage = async () => {
    if (!msgContent.trim() || !selectedResident) return;
    setSending(true);
    try {
      await api.post("/family/messages", { resident_id: selectedResident.id, content: msgContent });
      setMsgContent("");
      toast.success("Mensaje enviado");
      const r = await api.get(`/family/messages/${selectedResident.id}`);
      setMessages(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar");
    }
    setSending(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-slate-400">Cargando...</p>
    </div>
  );

  // If only one resident, show detail directly
  if (!selectedResident && residents.length === 1) {
    handleSelectResident(residents[0]);
    return <p className="text-slate-400 text-center py-12">Cargando...</p>;
  }

  // Resident selection view
  if (!selectedResident) {
    return (
      <div className="space-y-6 animate-fade-in-up" data-testid="family-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">
              Hola, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">Seleccioná un familiar para ver su información</p>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleDark} className="rounded-full w-9 h-9" data-testid="family-dark-toggle">
            {darkMode ? <SunIcon className="w-4 h-4 text-amber-400" /> : <MoonIcon className="w-4 h-4 text-slate-400" />}
          </Button>
        </div>

        {residents.length === 0 ? (
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-12 text-center">
              <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No tenés familiares vinculados aún</p>
              <p className="text-xs text-slate-400 mt-1">Contactá a la administración del geriátrico</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {residents.map(r => {
              const relLabels = { hijo: "Hijo/a", esposo: "Esposo/a", nieto: "Nieto/a", hermano: "Hermano/a", otro: "Otro" };
              return (
                <Card
                  key={r.id}
                  className="rounded-2xl border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-[1px]"
                  onClick={() => handleSelectResident(r)}
                  data-testid={`family-resident-${r.id}`}
                >
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-lg flex-shrink-0">
                      {r.name?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{r.name}</h3>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {r.room_number && <span className="text-sm text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Hab. {r.room_number}</span>}
                        <Badge className="rounded-full bg-slate-100 text-slate-600 border-0 text-xs">{relLabels[r.relationship] || r.relationship}</Badge>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Resident detail view
  const detail = residentDetail;
  const resident = detail?.resident || selectedResident;
  const vitals = detail?.vitals || [];
  const dailyMenu = detail?.daily_menu;
  const activities = detail?.activities || [];
  const dailyCareRecords = detail?.daily_care || [];

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="family-resident-view">
      {residents.length > 1 && (
        <Button variant="ghost" onClick={() => { setSelectedResident(null); setResidentDetail(null); }} className="rounded-xl text-slate-500 hover:text-slate-700 -ml-2" data-testid="back-to-residents">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver
        </Button>
      )}

      {/* Resident Card */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-xl">
              {resident?.name?.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-[Manrope]">{resident?.name}</h2>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {resident?.room_number && <span className="text-sm text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Hab. {resident?.room_number}</span>}
                <span className="text-sm text-slate-400 flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {resident?.obra_social}</span>
                <Badge className="rounded-full bg-slate-100 text-slate-600 border-0 text-xs font-medium">{depLabels[resident?.dependency_level] || resident?.dependency_level}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Menu */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <UtensilsCrossed className="w-5 h-5 text-amber-600" strokeWidth={1.8} />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm">Menú del Día</h3>
          </div>
          {dailyMenu ? (
            <div className="space-y-2">
              {Object.entries(dailyMenu).map(([meal, desc]) => (
                <div key={meal} className="bg-slate-50 rounded-xl px-4 py-3">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">{meal}</p>
                  <p className="text-sm text-slate-700 mt-0.5">{desc}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Menú aún no disponible para hoy</p>
          )}
        </CardContent>
      </Card>

      {/* Activities */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Activity className="w-5 h-5 text-emerald-600" strokeWidth={1.8} />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm">Actividades del Día</h3>
          </div>
          {activities.length > 0 ? (
            <div className="space-y-2">
              {activities.map((act, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                  <Clock className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  <p className="text-sm text-slate-700">{act}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No hay actividades programadas</p>
          )}
        </CardContent>
      </Card>

      {/* Daily Care — Weekly Dashboard */}
      <Card className="rounded-2xl border-slate-100 shadow-sm" data-testid="family-daily-care">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <Check className="w-5 h-5 text-teal-600" strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">Cuidado Diario</h3>
              <p className="text-xs text-slate-400">Evolución de los últimos 7 días</p>
            </div>
          </div>

          {/* Weekly Chart */}
          {weeklyData?.days && (
            <div className="mb-6">
              <div className="flex items-end gap-1.5 h-32" data-testid="weekly-care-chart">
                {weeklyData.days.map((day, i) => {
                  const dayName = new Date(day.date + "T12:00:00").toLocaleDateString("es-AR", { weekday: "short" });
                  const isToday = day.date === new Date().toISOString().split("T")[0];
                  const moodColor = day.mood === "bueno" ? "bg-emerald-400" : day.mood === "regular" ? "bg-amber-400" : day.mood === "malo" ? "bg-rose-400" : "bg-slate-200";
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`weekly-day-${i}`}>
                      <span className="text-[10px] text-slate-400 font-semibold">{day.percentage}%</span>
                      <div className="w-full bg-slate-100 rounded-lg relative" style={{ height: "80px" }}>
                        <div
                          className={`absolute bottom-0 w-full rounded-lg transition-all duration-500 ${day.shifts === 0 ? "bg-slate-200" : moodColor}`}
                          style={{ height: `${Math.max(day.percentage * 0.8, day.shifts > 0 ? 4 : 0)}px`, opacity: day.shifts === 0 ? 0.3 : 0.85 }}
                        />
                      </div>
                      <span className={`text-[10px] font-semibold ${isToday ? "text-violet-600" : "text-slate-400"}`}>{dayName}</span>
                      {isToday && <div className="w-1 h-1 rounded-full bg-violet-600" />}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-4 mt-3">
                <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" /> Bueno</span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400" /> Regular</span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400" /> Decaído</span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-slate-200" /> Sin datos</span>
              </div>
            </div>
          )}

          {/* Today's detail */}
          <div className="border-t border-slate-100 pt-4">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Detalle de hoy</h4>
            {dailyCareRecords.length > 0 ? (
              <div className="space-y-3">
                {dailyCareRecords.map((rec) => {
                  const shiftLabels = { manana: "Mañana", tarde: "Tarde", noche: "Noche" };
                  const items = rec.items || {};
                  const careLabels = {
                    desayuno: "Desayuno", almuerzo: "Almuerzo", merienda: "Merienda", cena: "Cena",
                    hidratacion: "Hidratación", bano: "Baño", higiene_personal: "Higiene",
                    movilizacion: "Movilización", descanso_nocturno: "Descanso"
                  };
                  const moodLabels = { bueno: "Bueno", regular: "Regular", malo: "Decaído" };
                  const moodColors = { bueno: "bg-emerald-50 text-emerald-700", regular: "bg-amber-50 text-amber-700", malo: "bg-rose-50 text-rose-600" };
                  const completed = Object.values(items).filter(Boolean).length;
                  return (
                    <div key={rec.id} className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className="rounded-full bg-violet-100 text-violet-700 border-0 text-[10px] font-semibold">
                            Turno {shiftLabels[rec.shift_type] || rec.shift_type}
                          </Badge>
                          <span className="text-[10px] text-slate-400 font-semibold">{completed}/9</span>
                        </div>
                        {rec.mood && (
                          <Badge className={`rounded-full text-[10px] font-semibold border-0 ${moodColors[rec.mood] || ""}`}>
                            Ánimo: {moodLabels[rec.mood] || rec.mood}
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(careLabels).map(([key, label]) => {
                          const done = items[key];
                          return (
                            <div key={key} className={`text-center rounded-lg py-2 text-xs font-medium ${done ? "bg-emerald-50 text-emerald-700" : "bg-white text-slate-400 border border-slate-100"}`}>
                              {done && <Check className="w-3 h-3 mx-auto mb-0.5" />}
                              {label}
                            </div>
                          );
                        })}
                      </div>
                      {rec.notes && <p className="text-xs text-slate-500 mt-3 leading-relaxed">{rec.notes}</p>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Aún no se registró el cuidado diario de hoy</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-violet-600" strokeWidth={1.8} />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm">Mensajes</h3>
          </div>

          <div className="space-y-3 max-h-[300px] overflow-y-auto mb-4">
            {messages.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No hay mensajes aún. Envíe su primera consulta.</p>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_type === "family" ? "justify-end" : "justify-start"}`}
                  data-testid={`fam-msg-${msg.id}`}
                >
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.sender_type === "family" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-900"
                  }`}>
                    <p className={`text-[10px] font-semibold mb-1 ${msg.sender_type === "family" ? "text-violet-200" : "text-slate-400"}`}>
                      {msg.sender_name}
                    </p>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${msg.sender_type === "family" ? "text-violet-300" : "text-slate-400"}`}>
                      {new Date(msg.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-slate-100 pt-4 flex gap-2 items-end">
            <Textarea
              value={msgContent}
              onChange={e => setMsgContent(e.target.value)}
              placeholder="Escribir mensaje o consulta..."
              className="rounded-xl bg-slate-50 min-h-[50px] border-slate-200 flex-1 resize-none text-sm"
              data-testid="family-msg-input"
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!msgContent.trim() || sending}
              className="rounded-xl h-10 px-4 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all"
              data-testid="family-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
